/**
 * Shared scaffold for the Intro and Roadmap pages: HeaderController, a
 * desktop two-column layout (label/heading/steps/CTA text column + hero image
 * column), and a mobile layout (hero banner + label/heading/steps + a fixed
 * sticky CTA with a white fade). The two pages differ in hero art, step
 * content, and desktop CTA styling, so those are supplied via props/render
 * functions while this component owns the shared structure and spacing.
 */

import React from "react";
import { useParams } from "react-router-dom";
import HeaderController from "../../../components/Headers/HeaderController";
import { clientConfig } from "../../../config";
import * as translator from "../../../utils/translator";
import Logo from "../../../images/logo-dark.svg";
import { ShellContext, ShellScopedId } from "./shellContext";

interface JourneyLayoutProps {
  label: React.ReactNode;
  heading: React.ReactNode;
  renderHeroDesktop: () => React.ReactNode;
  renderHeroMobile: () => React.ReactNode;
  renderSteps: (compact: boolean) => React.ReactNode;
  ctaText: string;
  ctaTextMobile?: string;
  onStart: () => void;
  /** Mobile back arrow. Defaults to leaving the page via browser history (the intro). */
  onBack?: () => void;
  ctaDesktopClassName: string;
  ctaDesktopStyle?: React.CSSProperties;
  headingDesktopMarginClassName?: string;
  desktopWrapperClassName?: string;
  desktopGapClassName?: string;
  heroDesktopWrapperClassName?: string;
  /** Defaults to width-tier fixed heights (Intro art). Roadmap passes natural height so the full product image shows. */
  heroMobileWrapperClassName?: string;
  /** Intro keeps the top banner; Roadmap places the product image under the DoktorABC logo. */
  mobileHeroPlacement?: "before-logo" | "after-logo";
  /** DOM ids for the page root and primary CTA. Both blocks render, so the
   *  desktop block keeps them as-is and the mobile block adds `_mobile`. */
  pageId?: string;
  ctaId?: string;
}

const JourneyLayout: React.FC<JourneyLayoutProps> = ({
  label,
  heading,
  renderHeroDesktop,
  renderHeroMobile,
  renderSteps,
  ctaText,
  ctaTextMobile,
  onStart,
  onBack = () => window.history.back(),
  ctaDesktopClassName,
  ctaDesktopStyle,
  headingDesktopMarginClassName = "mb-5 2xl:mb-7",
  // Cap to one viewport under the header so the default view does not open scrolled.
  // overflow-y-auto is a safety valve for zoom / long copy; normal 1080p should not scroll.
  // py lives here so pages can override vertical padding via desktopWrapperClassName.
  desktopWrapperClassName = "h-[calc(100vh-78px)] max-h-[calc(100vh-78px)] min-h-0 py-4 2xl:py-6 overflow-y-auto",
  desktopGapClassName = "gap-8 xl:gap-[64px] 2xl:gap-[96px]",
  heroDesktopWrapperClassName = "flex-1 min-w-0 max-w-[480px] xl:max-w-[560px] max-h-full min-h-0 flex items-center justify-center",
  // Short phones (iPhone SE) drop the hero so the steps stay above the fixed CTA.
  heroMobileWrapperClassName = "relative w-full shrink-0 overflow-hidden h-[min(180px,32dvh)] min-[380px]:h-[min(230px,34dvh)] min-[560px]:h-[min(275px,36dvh)] [@media(max-height:700px)]:hidden",
  mobileHeroPlacement = "before-logo",
  pageId,
  ctaId,
}) => {
  const { country } = useParams<{ country?: string }>();
  const homeUrl = `${clientConfig.MAIN_WEBSITE_URL}/${translator.getCMSUrlPrefixByLocale(
    translator.getLocaleByCountry(country)
  )}`;
  const heroAfterLogo = mobileHeroPlacement === "after-logo";
  const backButton = (
    <ShellScopedId base="wl2_back_button">
      {(backId) => (
        <button
          id={backId}
          onClick={onBack}
          aria-label="Back"
          className="w-6 h-6 flex items-center justify-center text-[#99a3b2] flex-shrink-0 cursor-pointer hover:text-[#333] active:scale-95 transition"
          style={{ border: "none", background: "transparent", padding: 0 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </ShellScopedId>
  );
  // Top-banner hero runs to the top edge with the back arrow overlaid on it.
  const mobileHero = (
    <div className={heroMobileWrapperClassName}>
      {renderHeroMobile()}
      {!heroAfterLogo && <div className="absolute top-4 left-5 z-10">{backButton}</div>}
    </div>
  );

  return (
  <>
    <div className="hidden lg:block">
      <HeaderController showSteps={false} stage="Questionnaire" isCannabis={false} />
    </div>

    {/* ============ DESKTOP (lg+) ============ */}
    <ShellContext.Provider value="desktop">
    <ShellScopedId base={pageId}>
      {(desktopPageId) => (
    <div id={desktopPageId} className={`hidden lg:flex items-center justify-center w-full px-6 ${desktopWrapperClassName}`}>
      <div className={`flex items-center justify-center ${desktopGapClassName} max-w-[1280px] 2xl:max-w-[1418px] w-full max-h-full min-h-0`}>
        {/* basis+max-w lets the text column shrink on smaller desktops instead of pushing the hero out. */}
        <div className="flex flex-col lg:basis-[480px] xl:basis-[540px] 2xl:basis-[600px] max-w-[580px] 2xl:max-w-[600px] min-w-0 shrink">
          <p className="font-['Poppins'] text-[16px] leading-[24px] xl:text-[18px] xl:leading-[28px] 2xl:text-[20px] 2xl:leading-[30px] text-[#333]">
            {label}
          </p>
          <ShellScopedId base="wl2_journey_heading">
            {(headingId) => (
              <h1
                id={headingId}
                className={`font-['Poppins'] font-semibold text-[32px] leading-[38px] xl:text-[40px] xl:leading-[48px] 2xl:text-[46px] 2xl:leading-[54px] text-[#333] max-w-[558px] 2xl:max-w-[600px] mt-2 ${headingDesktopMarginClassName}`}
              >
                {heading}
              </h1>
            )}
          </ShellScopedId>
          {renderSteps(false)}
          <ShellScopedId base={ctaId}>
            {(desktopCtaId) => (
              <button id={desktopCtaId} onClick={onStart} className={ctaDesktopClassName} style={ctaDesktopStyle}>
                {ctaText}
              </button>
            )}
          </ShellScopedId>
        </div>

        <div className={heroDesktopWrapperClassName}>{renderHeroDesktop()}</div>
      </div>
    </div>
      )}
    </ShellScopedId>
    </ShellContext.Provider>

    {/* ============ MOBILE (<lg) ============ */}
    {/* min-h-dvh after min-h-screen: dynamic toolbars on supporting browsers; 100vh fallback elsewhere. */}
    <ShellContext.Provider value="mobile">
    <ShellScopedId base={pageId}>
      {(mobilePageId) => (
    <div id={mobilePageId} className="flex lg:hidden flex-col min-h-screen min-h-dvh">
      {heroAfterLogo && <div className="px-5 pt-4 shrink-0">{backButton}</div>}

      {!heroAfterLogo && mobileHero}

      <div className="px-5 pt-4 pb-32">
        <ShellScopedId base="wl2_logo_link">
          {(logoId) => (
            <a id={logoId} href={homeUrl} aria-label="DoktorABC">
              <img
                src={Logo}
                alt="DoktorABC"
                className={`h-[22px] w-auto ${heroAfterLogo ? "mb-4" : "mb-7"}`}
              />
            </a>
          )}
        </ShellScopedId>
        {/* Figma: ~8px between the Roadmap image and the label under it. */}
        {heroAfterLogo && <div className="mb-2">{mobileHero}</div>}
        <p className="font-['Poppins'] text-[16px] text-[#333]">{label}</p>
        <ShellScopedId base="wl2_journey_heading">
          {(headingId) => (
            <h1 id={headingId} className="font-['Poppins'] font-semibold text-[28px] leading-[34px] text-[#333] mt-1 mb-6">
              {heading}
            </h1>
          )}
        </ShellScopedId>
        {renderSteps(true)}
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 z-40 px-5 pt-8 pb-5"
        style={{
          backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0), #fff 30%)",
          // Keep the CTA clear of the iOS/Android home indicator.
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
        }}
      >
        <ShellScopedId base={ctaId}>
          {(mobileCtaId) => (
            <button
              id={mobileCtaId}
              onClick={onStart}
              className="w-full h-[54px] rounded-[16px] text-white font-['Poppins'] font-medium text-[18px] cursor-pointer active:opacity-80 transition"
              style={{ backgroundImage: "linear-gradient(90deg, #f59200, #ffbb29)", border: "none" }}
            >
              {ctaTextMobile ?? ctaText}
            </button>
          )}
        </ShellScopedId>
      </div>
    </div>
      )}
    </ShellScopedId>
    </ShellContext.Provider>
  </>
);
};

export default JourneyLayout;
